import type { FC, ReactNode } from 'react'
import type { FaqItem } from '@/types/content'

import { useId, useState } from 'react'
import { Plus } from 'lucide-react'
import { cn } from '@/lib/utils'
import { DraftBadge } from '@/components/sections/draft-badge'
import { cms, hasValue, isPlaceholder } from '@/lib/cms'

interface FaqListProps {
    items: Array<FaqItem>
    defaultOpen?: boolean
    className?: string
}

// Accessible accordion for questions from the "faq" collection. Answers stay in the page for search engines.
export const FaqList: FC<FaqListProps> = ({ items, defaultOpen = false, className }): ReactNode => {
    const baseId = useId()
    const [openId, setOpenId] = useState<string | null>(defaultOpen ? items[0]?.id ?? null : null)

    if (items.length === 0) return null

    return (
        <div className={cn('border-t border-ink/15', className)}>
            {items.map(item => {
                const open = openId === item.id
                const buttonId = `${baseId}-frage-${item.id}`
                const panelId = `${baseId}-antwort-${item.id}`

                return (
                    <div key={item.id} className='border-b border-ink/15'>
                        <h3>
                            <button
                                id={buttonId}
                                type='button'
                                aria-expanded={open}
                                aria-controls={panelId}
                                onClick={() => setOpenId(open ? null : item.id)}
                                className='group flex w-full items-start justify-between gap-6 py-6 text-left sm:py-7'
                            >
                                <span
                                    data-mdlfy-cms={cms('faq', item.id, 'frage')}
                                    className='text-lg font-semibold leading-snug tracking-[-0.01em] text-ink transition-colors duration-300 group-hover:text-ember sm:text-xl'
                                >
                                    {item.frage}
                                </span>

                                <span
                                    aria-hidden='true'
                                    className={cn(
                                        'mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-full border transition-[transform,background-color,border-color,color] duration-500 ease-brand',
                                        open ? 'rotate-45 border-ink bg-ink text-bone' : 'border-ink/20 text-ink group-hover:border-ink/50'
                                    )}
                                >
                                    <Plus className='size-4' strokeWidth={2} />
                                </span>
                            </button>
                        </h3>

                        <div
                            id={panelId}
                            role='region'
                            aria-labelledby={buttonId}
                            inert={!open}
                            className={cn('grid transition-[grid-template-rows] duration-500 ease-brand', open ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]')}
                        >
                            <div className='overflow-hidden'>
                                <div className='flex flex-col items-start gap-4 pb-8 pr-6 sm:pr-14'>
                                    {hasValue(item.antwort) && (
                                        <p
                                            data-mdlfy-cms={cms('faq', item.id, 'antwort')}
                                            className={cn('max-w-2xl whitespace-pre-line text-base leading-relaxed text-smoke', isPlaceholder(item.antwort) && 'is-placeholder')}
                                        >
                                            {item.antwort}
                                        </p>
                                    )}

                                    {!item.bestaetigt && <DraftBadge label='Antwort noch nicht bestätigt' />}
                                </div>
                            </div>
                        </div>
                    </div>
                )
            })}
        </div>
    )
}
